// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ReviewPreflight.test.tsx
import { Checkbox, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { QuestionFormCard } from "./QuestionFormCard";

import {
  agentComponentIdAttr,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../agentComponents/chatAtomRegistry";

export type ReviewPreflightCategory = {
  id: string;
  label: string;
  description?: string;
  /** Nested under a parent row (Figma scope: Nutrition / Product / Region). */
  indent?: boolean;
};

export type ReviewPreflightProps = {
  variant?: "confirm" | "scope";
  categories: ReviewPreflightCategory[];
  selectedCategoryIds?: string[];
  onToggleCategory?: (id: string) => void;
  onStart: () => void;
  onChangeScope: () => void;
  onCancel?: () => void;
  summary?: string;
  description?: string;
  /** The always-offered Other row; the focus field shows while it is checked. */
  otherChecked?: boolean;
  onToggleOther?: () => void;
  focusValue?: string;
  onFocusChange?: (value: string) => void;
  testIdPrefix?: string;
  /** Gray out controls after Start or while a send is in flight. */
  disabled?: boolean;
  /** Persisted submit — keeps Start and checkboxes disabled after refresh. */
  submitted?: boolean;
  /** False on history turns; checkboxes and Start ignore input when false. */
  interactive?: boolean;
};

const PANEL_CLASS =
  "flex flex-col gap-2 rounded-xl border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2 p-2";

/**
 * Pre-review confirm / scope picker. Customer-facing scope copy only — no fake
 * progress chrome (Figma ReviewPreflight 49613:704).
 */
export function ReviewPreflight({
  variant = "confirm",
  categories,
  selectedCategoryIds = [],
  onToggleCategory,
  onStart,
  onChangeScope,
  onCancel,
  summary,
  description,
  otherChecked = false,
  onToggleOther,
  focusValue,
  onFocusChange,
  testIdPrefix = "review-preflight",
  disabled = false,
  submitted = false,
  interactive = true,
}: ReviewPreflightProps) {
  const { t } = useTranslation("ui");
  const selected = categories.filter((c) => selectedCategoryIds.includes(c.id));
  const confirmTitle =
    summary ??
    (selected.map((c) => c.label).join(", ") || t("reviewPreflight.title"));
  const confirmDescription =
    description ?? t("reviewPreflight.confirmDescription");
  const scopeTitle = summary ?? t("reviewPreflight.scopeTitle");
  const scopeDescription = description ?? t("reviewPreflight.scopeDescription");
  const isScope = variant === "scope";
  const controlsLocked = !interactive || disabled || submitted;

  return (
    <div
      {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.reviewPreflight)}
      data-testid={testIdPrefix}
      className={PANEL_CLASS}
    >
      <QuestionFormCard
        chrome="inner"
        testId={`${testIdPrefix}-form`}
        testIdPrefix={testIdPrefix}
        detail1={isScope ? scopeTitle : confirmTitle}
        detail2={isScope ? scopeDescription : confirmDescription}
        primary={{
          action: "start",
          testId: `${testIdPrefix}-start`,
          label: t("reviewPreflight.startReview"),
          onClick: onStart,
          disabled: controlsLocked,
        }}
        tertiary={
          isScope
            ? onCancel
              ? {
                  action: "cancel",
                  testId: `${testIdPrefix}-cancel`,
                  label: t("reviewPreflight.cancel"),
                  onClick: onCancel,
                  disabled: controlsLocked,
                }
              : undefined
            : {
                action: "change-scope",
                testId: `${testIdPrefix}-change-scope`,
                label: t("reviewPreflight.changeScope"),
                onClick: onChangeScope,
                disabled: controlsLocked,
              }
        }
      >
        {isScope ? (
          <div className="flex flex-col gap-2">
            {categories.map((category) => {
              const checked = selectedCategoryIds.includes(category.id);
              return (
                <label
                  key={category.id}
                  data-testid={`${testIdPrefix}-scope-${category.id}`}
                  className={
                    category.indent
                      ? "flex cursor-pointer items-start gap-2 pl-6"
                      : "flex cursor-pointer items-start gap-2"
                  }
                >
                  <Checkbox
                    checked={checked}
                    disabled={controlsLocked}
                    onCheckedChange={() => {
                      if (!controlsLocked) onToggleCategory?.(category.id);
                    }}
                    aria-label={category.label}
                    size="2"
                  />
                  <span className="flex min-w-0 flex-col">
                    <Text size="2" className="text-puntt-neutral-gray-12">
                      {category.label}
                    </Text>
                    {category.description ? (
                      <Text size="1" className="text-puntt-neutral-gray-11">
                        {category.description}
                      </Text>
                    ) : null}
                  </span>
                </label>
              );
            })}
            <label
              data-testid={`${testIdPrefix}-scope-other`}
              className="flex cursor-pointer items-start gap-2"
            >
              <Checkbox
                checked={otherChecked}
                disabled={controlsLocked}
                onCheckedChange={() => {
                  if (!controlsLocked) onToggleOther?.();
                }}
                aria-label={t("reviewPreflight.other")}
                size="2"
              />
              <Text size="2" className="text-puntt-neutral-gray-12">
                {t("reviewPreflight.other")}
              </Text>
            </label>
            {otherChecked ? (
              <div className="flex flex-col gap-2 pl-6">
                <Text size="1" className="text-puntt-neutral-gray-11">
                  {t("reviewPreflight.focusLabel")}
                </Text>
                <input
                  data-testid={`${testIdPrefix}-focus`}
                  aria-label={t("reviewPreflight.focusLabel")}
                  placeholder={t("reviewPreflight.focusPlaceholder")}
                  value={focusValue ?? ""}
                  disabled={controlsLocked}
                  onChange={(e) => onFocusChange?.(e.target.value)}
                  className="h-9 w-full rounded-lg border border-puntt-neutral-gray-4 bg-base-white px-2 text-[14px] text-puntt-neutral-gray-12 outline-none focus:border-puntt-accent-8 disabled:cursor-not-allowed disabled:opacity-60"
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </QuestionFormCard>
    </div>
  );
}
